import { TrashIcon } from '@phosphor-icons/react';
import { PageHeader } from 'components/PageHeader';
import { IncassoStatus } from 'src/types/enums';
import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { invariant, nullthrows } from '@utils/invariant';
import { captureException } from '@sentry/react';
import { trackEvent } from '../../../../utils/track-event';

import { Breadcrumb } from '../../../../components/Breadcrumb';
import { DataField } from '../../../../components/DataField';
import { ConfirmDialog } from '../../../../components/dialog/ConfirmDialog';
import { DelayedSpinner } from '../../../../components/Spinner';
import { formatDate } from '../../../../utils/date';
import { getDisplayError } from '../../../../utils/get-display-error';
import { decimateAndFormatNumber } from '../../../../utils/number';
import { useAuth } from '../../../../contexts/auth-context';
import { InternalRole } from '../../../auth/endpoints/MeEndpoint.types';
import { roleToNumber } from '../../../auth/roles.client';
import { PageHeading } from 'components/PageHeading';
import { SummaryBar } from 'components/SummaryBar';
import { MultiFileUploaderButton } from 'src/app/document/components/MultiFileUploaderButton';
import { TextArea } from 'components/textarea/TextArea';
import {
  deleteIncasso,
  linkIncassoDocument,
  unlinkIncassoDocument,
  updateIncasso,
  useIncasso,
  type IncassoDetail,
} from '../../models';

export type IncassoInvoiceItem = IncassoDetail['invoices'][number];

export interface IncassoInvoiceProps {
  incasso: IncassoDetail;
  invoice: IncassoInvoiceItem;
  refresh: () => Promise<unknown>;
}

const IncassoInvoice: React.FC<IncassoInvoiceProps> = (props) => {
  const { incasso, invoice, refresh } = props;
  const [notes, setNotes] = useState<string>(invoice.notes || '');

  return (
    <div key={`invoice-${invoice.id}`} className="card">
      <div className="card-heading">
        <Link to={`/internal/finance/sales/${invoice.invoice.id}`} className="link-text">
          Factuur {invoice.invoice.invoiceNumber}
        </Link>
      </div>

      <div className="card-content flex flex-col gap-2">
        <div className="grid grid-cols-2 gap-x-4 text-sm">
          <div className="label-text">Datum:</div>
          <div>{formatDate(invoice.invoice.date)}</div>
        </div>
        <div className="grid grid-cols-2 gap-x-4 text-sm">
          <div className="label-text">Vervaldag:</div>
          <div>{formatDate(invoice.invoice.expiresAt)}</div>
        </div>
        <div className="grid grid-cols-2 gap-x-4 text-sm">
          <div className="label-text">Totaal incl. btw:</div>
          <div>€ {decimateAndFormatNumber(invoice.invoice.totalInclVat, 2, { decimalSeperator: ',' })}</div>
        </div>

        <div className="mt-1">
          <div className="label-text mb-1.5">Notities:</div>
          <TextArea
            rows={4}
            value={notes}
            onChange={setNotes}
            onBlur={async () => {
              try {
                await updateIncasso(incasso.id, {
                  invoices: [
                    {
                      invoiceId: invoice.invoice.id,
                      notes,
                    },
                  ],
                });
                await refresh();
                toast.success('Notities opgeslagen');
              } catch (err) {
                captureException(err);
                toast.error(`Kon notities niet opslaan: ${getDisplayError(err)}`);
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};

const IncassoPage = () => {
  const { me } = useAuth();
  const navigate = useNavigate();
  const { incassoId } = useParams<{ incassoId: string }>();
  invariant(incassoId);
  const { data, error, isLoading, mutate: refresh } = useIncasso(Number(incassoId));

  const totalInclVat =
    data?.invoices.reduce((acc, invoice) => {
      return acc + invoice.invoice.totalInclVat;
    }, 0) ?? 0;

  if (isLoading) {
    return <DelayedSpinner size={8} />;
  }

  const incasso = nullthrows(data, 'Incasso niet gevonden');
  const title = `Incasso ${incasso.trackingId}`;

  if (error) {
    captureException(error);
    return <div className="p-4">Kon incasso niet laden: {getDisplayError(error)}</div>;
  }

  return (
    <>
      <PageHeader title={title} />

      <PageHeading
        noMarginBottom
        leftSide={
          <Breadcrumb
            parentItem={{
              name: 'Incassos',
              to: '/internal/finance/incasso',
            }}
            currentItem={title}
          />
        }
        rightSide={
          <div className="flex gap-2">
            <ConfirmDialog
              triggerText={
                incasso.status === IncassoStatus.Created ? 'Markeer als opgelost' : 'Markeer als in behandeling'
              }
              title={
                incasso.status === IncassoStatus.Created ? 'Incasso proces volbracht?' : 'Incasso process nog bezig?'
              }
              submitText="Wijzig status"
              description={<div>Ben je zeker dat je de status van het incasso proces wilt wijzigen?</div>}
              onSubmit={async () => {
                try {
                  const newStatus =
                    incasso.status === IncassoStatus.Created ? IncassoStatus.Resolved : IncassoStatus.Created;
                  await updateIncasso(Number(incassoId), {
                    status: newStatus,
                  });
                  await refresh();
                  trackEvent('incasso_status_changed', {
                    incasso_id: incasso.id,
                    new_status: newStatus,
                  });
                  toast.success('Incasso status aangepast');
                } catch (err) {
                  captureException(err);
                  toast.error(`Kon incasso status niet aanpassen: ${getDisplayError(err)}`);
                }
              }}
            />

            <MultiFileUploaderButton
              buttonText="Bestanden"
              title="Bestanden"
              files={incasso.documents.map((f) => f.document)}
              onSubmit={async (documents) => {
                let count = 0;
                await Promise.allSettled(
                  documents.map(async (doc) => {
                    try {
                      await linkIncassoDocument(incasso.id, doc.id);
                      count += 1;
                      toast.success(`Bestand: ${doc.name}, ${count}/${documents.length} opgeladen`);
                    } catch (err) {
                      captureException(err);
                      toast.error(`Kon bestand ${doc.name} niet opladen: ${getDisplayError(err)}`);
                    }
                  }),
                );
                await refresh();
              }}
              unlinkDocument={async (docId: string) => {
                try {
                  await unlinkIncassoDocument(incasso.id, docId);
                  await refresh();
                  toast.success('Bestand gewist');
                } catch (err) {
                  captureException(err);
                  toast.error(`Kon bestand niet wissen: ${getDisplayError(err)}`);
                }
              }}
            />

            {roleToNumber(me.role) >= roleToNumber(InternalRole.InternalAdmin) && (
              <ConfirmDialog
                triggerColor="danger"
                triggerText={<TrashIcon className="button-icon" />}
                title="incasso verwijderen?"
                submitText="Verwijder incasso"
                description={
                  <div>
                    Ben je zeker dat je dit credit nota wilt verwijderen? Dit kan ervoor zorgen dat de credit nota
                    nummering niet meer opeenlopend zijn.
                  </div>
                }
                onSubmit={async () => {
                  try {
                    await deleteIncasso(Number(incassoId));
                    trackEvent('incasso_deleted', { incasso_id: incasso.id });
                    toast.success('Incasso verwijderd');
                    navigate('..');
                  } catch (err) {
                    captureException(err);
                    toast.error(`Kon incasso niet verwijderen: ${getDisplayError(err)}`);
                  }
                }}
              />
            )}
          </div>
        }
      />

      <div className="flex flex-col gap-4 p-7">
        <SummaryBar>
          <DataField title="Status">
            {incasso.status === IncassoStatus.Created ? 'In behandeling' : 'Volbracht'}
          </DataField>
          <DataField title="ID">{incasso.internalNumber}</DataField>
          <DataField title="Tracking ID">{incasso.trackingId}</DataField>
          <DataField title="Klant">
            {incasso.customer.name} ({incasso.customer.id})
          </DataField>
          <DataField title="Partner">{incasso.partner}</DataField>
          <DataField title="Aanmaak datum">{formatDate(incasso.createdAt)}</DataField>
        </SummaryBar>

        <>
          {incasso.invoices
            .sort((a, b) => {
              return new Date(a.invoice.date ?? 0).getTime() - new Date(b.invoice.date ?? 0).getTime();
            })
            .map((invoice) => {
              return <IncassoInvoice key={invoice.id} incasso={incasso} invoice={invoice} refresh={refresh} />;
            })}
        </>

        <div className="flex items-center gap-3 px-5">
          <div className="min-w-0 flex-1 text-xs text-fg-muted">Algemeen totaal incl. btw:</div>
          <div className="whitespace-nowrap text-sm font-semibold text-fg">
            €{' '}
            {decimateAndFormatNumber(totalInclVat, 2, {
              decimalSeperator: ',',
            })}
          </div>
        </div>
      </div>
    </>
  );
};

export default IncassoPage;
